import {
  AmbientLight,
  Box3,
  Color,
  DirectionalLight,
  Group,
  Mesh,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  ShadowMaterial,
  Sphere,
  Vector3,
  WebGLRenderer,
  ACESFilmicToneMapping,
  SRGBColorSpace,
} from 'three';
import { config } from '../config';

/** One inset slot — a screen-space rect that renders a close-up of a specific ball. */
interface InsetSlot {
  /** World-space point the inset camera looks at. null = slot inactive. */
  target: Vector3 | null;
  /** Dedicated narrow-FOV camera that frames the target ball. */
  camera: PerspectiveCamera;
  /** CSS-style rect (top-left origin) where the inset is rendered on the canvas. */
  rect: { x: number; y: number; w: number; h: number };
}

export class SceneRig {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly contentRoot: Group;

  private readonly container: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly resizeObserver: ResizeObserver;
  private readonly keyLight: DirectionalLight;
  private readonly floor: Mesh<PlaneGeometry, ShadowMaterial>;
  private readonly lookAt = new Vector3();
  private insets: InsetSlot[] = [];

  constructor(container: HTMLElement, opts: { mobileMode: boolean }) {
    this.container = container;

    this.renderer = new WebGLRenderer({
      antialias: !opts.mobileMode,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = config.renderer.toneMappingExposure;
    this.renderer.shadowMap.enabled = !opts.mobileMode;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    const cap = opts.mobileMode ? config.renderer.pixelRatioCapMobile : config.renderer.pixelRatioCap;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, cap));

    this.canvas = this.renderer.domElement;
    this.canvas.style.display = 'block';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    container.appendChild(this.canvas);

    this.scene = new Scene();
    this.scene.background = new Color(config.renderer.backgroundColor);

    this.camera = new PerspectiveCamera(
      config.camera.fov,
      1,
      config.camera.near,
      config.camera.far,
    );
    this.camera.position.set(0, 2, 8);
    this.camera.lookAt(0, 1, 0);

    this.contentRoot = new Group();
    this.contentRoot.name = 'contentRoot';
    this.scene.add(this.contentRoot);

    const ambient = new AmbientLight(config.lights.ambient.color, config.lights.ambient.intensity);
    this.scene.add(ambient);

    this.keyLight = new DirectionalLight(config.lights.key.color, config.lights.key.intensity);
    this.keyLight.position.set(...config.lights.key.position);
    this.keyLight.castShadow = config.lights.key.castShadow && !opts.mobileMode;
    if (this.keyLight.castShadow) {
      this.keyLight.shadow.mapSize.set(2048, 2048);
      this.keyLight.shadow.bias = -0.0008;
      this.keyLight.shadow.normalBias = 0.02;
      const sc = this.keyLight.shadow.camera;
      sc.left = -8;
      sc.right = 8;
      sc.top = 8;
      sc.bottom = -8;
      sc.near = 0.1;
      sc.far = 30;
    }
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);

    const fill = new DirectionalLight(config.lights.fill.color, config.lights.fill.intensity);
    fill.position.set(...config.lights.fill.position);
    this.scene.add(fill);

    const rim = new DirectionalLight(config.lights.rim.color, config.lights.rim.intensity);
    rim.position.set(...config.lights.rim.position);
    this.scene.add(rim);

    const floorGeom = new PlaneGeometry(config.floor.size, config.floor.size);
    floorGeom.rotateX(-Math.PI / 2);
    const floorMat = new ShadowMaterial({ opacity: config.floor.shadowOpacity });
    this.floor = new Mesh(floorGeom, floorMat);
    this.floor.receiveShadow = !opts.mobileMode;
    this.scene.add(this.floor);

    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(container);
    this.handleResize();
  }

  /** Position the camera based on the loaded content's bounds and machine count. */
  frameMachine(opts: { centerY: number; sphereRadius: number; sicbo: boolean }): void {
    const { centerY, sphereRadius, sicbo } = opts;
    const multiplier = sicbo ? config.camera.distanceMultiplierSicbo : config.camera.distanceMultiplier;
    const dist = sphereRadius * multiplier;
    this.camera.position.set(0, centerY + sphereRadius * 0.35, dist);
    this.lookAt.set(0, centerY, 0);
    this.camera.lookAt(this.lookAt);
    this.keyLight.target.position.set(0, centerY, 0);
    this.keyLight.target.updateMatrixWorld();
  }

  /** Aim the floor at the base of whatever was loaded. */
  alignFloor(yWorld: number): void {
    this.floor.position.y = yWorld;
  }

  /** World-space bounding info for the content root. */
  getContentBounds(): { box: Box3; sphere: Sphere } {
    const box = new Box3().setFromObject(this.contentRoot);
    const sphere = box.getBoundingSphere(new Sphere());
    return { box, sphere };
  }

  /** Build N inset slots, each with its own camera and a CSS-style rect on the canvas. */
  configureInsets(rects: ReadonlyArray<{ x: number; y: number; w: number; h: number }>): void {
    this.insets = rects.map((r) => ({
      target: null,
      camera: new PerspectiveCamera(25, r.w / r.h, 0.05, config.camera.far),
      rect: { ...r },
    }));
  }

  /**
   * Aim the inset camera at slot `index` at a world-space point. Pass null to clear that slot.
   * Camera orbits with a small frontal-quarter offset around the target so the digit face is
   * in view without being dead-on.
   */
  setInsetTarget(index: number, target: Vector3 | null, ballRadius: number): void {
    const slot = this.insets[index];
    if (!slot) return;
    if (target === null) {
      slot.target = null;
      return;
    }
    slot.target = target.clone();
    const halfFov = (slot.camera.fov * Math.PI) / 360;
    const fitDist = (ballRadius * 1.6) / Math.tan(halfFov);
    const dir = new Vector3(0.55, 0.35, 0.78).normalize().multiplyScalar(fitDist);
    slot.camera.position.copy(target).add(dir);
    slot.camera.lookAt(target);
    slot.camera.updateProjectionMatrix();
  }

  clearAllInsets(): void {
    for (const s of this.insets) s.target = null;
  }

  render(): void {
    // Main pass: full canvas.
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setScissorTest(false);
    this.renderer.setViewport(0, 0, w, h);
    this.renderer.render(this.scene, this.camera);

    // Inset passes (one per active slot). Three.js viewport/scissor use bottom-up Y.
    let anyActive = false;
    for (const slot of this.insets) {
      if (!slot.target) continue;
      anyActive = true;
      const r = slot.rect;
      const x = Math.max(0, r.x);
      const y = Math.max(0, h - r.y - r.h);
      this.renderer.setScissorTest(true);
      this.renderer.setScissor(x, y, r.w, r.h);
      this.renderer.setViewport(x, y, r.w, r.h);
      this.renderer.render(this.scene, slot.camera);
    }
    if (anyActive) {
      this.renderer.setScissorTest(false);
      this.renderer.setViewport(0, 0, w, h);
    }
  }

  private handleResize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (w === 0 || h === 0) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.scene.traverse((obj) => {
      if (obj instanceof Mesh) {
        obj.geometry.dispose();
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats) m.dispose();
      }
    });
    this.renderer.dispose();
    this.canvas.remove();
  }
}
